import type { OptionsSelectList } from '../state/master';
import {
  normalizeEmail,
  normUpper,
  toNum,
  uniq,
  type UserInfoRow,
} from '../components/common/utils/SearchArea.utils';

/**
 * 課の選択肢（/get_department）まわりの共通ロジック。
 * InputEdit/SearchArea.tsx 内の同名ロジックと同じ判定順にしている（SearchArea 側は未置き換え）。
 */

/**
 * division の表示名から department を解決する。
 *
 * 重要:
 * - Test / test のように大文字小文字だけが違う課が存在する。
 * - department_id 完全一致 → value 完全一致 → label 完全一致を優先し、
 *   大文字小文字無視の比較は「候補が1件だけ」の場合に限定する。
 */
export const findDivisionOptionByStableIdentity = <T extends Partial<OptionsSelectList>>(
  divisionValue: unknown,    // 探したい課の値（value/label/null どれでも可） 
  options: T[] | undefined | null   // 課の選択肢の配列
): T | undefined => {

  // 前後の空白を除去し、空欄・選択肢が0件の場合は終了
  const rawKey = String(divisionValue ?? '').trim();
  const src = Array.isArray(options) ? options : [];
  if (!rawKey || src.length === 0) return undefined;

  const optionDeptId = (opt: any): string => String(opt?.department_id ?? '').trim();
  const optionValue = (opt: any): string => String(opt?.value ?? '').trim();
  const optionLabel = (opt: any): string => String(opt?.label ?? '').trim();

  // 1) department_id が渡っている場合は最優先。課名重複でも絶対にズレない。
  const byId = src.find((opt) => optionDeptId(opt) !== '' && optionDeptId(opt) === rawKey);
  if (byId) return byId;

  // 2) select の value 完全一致。Test と test を別物として扱う。
  const byExactValue = src.find((opt) => optionValue(opt) !== '' && optionValue(opt) === rawKey);
  if (byExactValue) return byExactValue;

  // 3) label 完全一致。value が空でも label から正しく引けるようにする。
  const byExactLabel = src.find((opt) => optionLabel(opt) !== '' && optionLabel(opt) === rawKey);
  if (byExactLabel) return byExactLabel;

  // 4) 最後の保険。大文字小文字無視は、候補が一意に決まる場合だけ許可する。
  const upperKey = normUpper(rawKey);
  const caseInsensitiveMatches = src.filter(
    (opt) =>
      normUpper(optionValue(opt)) === upperKey ||
      normUpper(optionLabel(opt)) === upperKey ||
      optionDeptId(opt) === rawKey
  );

  if (caseInsensitiveMatches.length === 1) return caseInsensitiveMatches[0];

  const matchedDepartmentIds = new Set(
    caseInsensitiveMatches.map((opt) => optionDeptId(opt)).filter(Boolean)
  );
  if (matchedDepartmentIds.size === 1) return caseInsensitiveMatches[0];

  // Test / test のように複数候補がある場合は、誤った department_id を選ばない。
  return undefined;
};

export type OwnDivisionContext = {
  /** ログインユーザーのメール（Cognito → loginId → profile の順で解決済みのもの） */
  email: string;
  /** /user-info の users */
  users: UserInfoRow[];
  /** profile の課名（profile.division ?? profile.department） */
  profileDivision?: string;
};

/**
 * 自分の課に対応する選択肢を解決する。
 *   1) user-info の department_id で一致（最優先）
 *   2) profile の課名で一致
 *   3) user-info の課名で一致
 * メールが特定できない場合は、全ユーザーの課を候補にしないよう user-info を使わない。
 */
export const resolveOwnDivisionOption = <T extends Partial<OptionsSelectList>>(
  list: T[],
  { email, users, profileDivision }: OwnDivisionContext
): T | null => {
  if (!Array.isArray(list) || list.length === 0) return null;

  const targetEmail = String(email ?? '').trim();
  const mine = targetEmail
    ? (users ?? []).filter(
        (u) => normalizeEmail(u?.mail_address) === normalizeEmail(targetEmail)
      )
    : [];

  const deptIds = uniq(
    mine
      .map((u) => (u?.department_id == null ? undefined : toNum(u.department_id)))
      .filter((v): v is number => typeof v === 'number')
  );
  const deptNames = uniq(
    mine.map((u) => String(u?.department ?? '').trim()).filter(Boolean)
  );

  let hit: T | undefined =
    deptIds.length > 0
      ? list.find((opt: any) => {
          const id = toNum(opt?.department_id);
          return typeof id === 'number' && deptIds.includes(id);
        })
      : undefined;

  if (!hit && profileDivision) {
    hit = findDivisionOptionByStableIdentity(profileDivision, list);
  }

  if (!hit) {
    for (const deptName of deptNames) {
      hit = findDivisionOptionByStableIdentity(deptName, list);
      if (hit) break;
    }
  }

  return hit ?? null;
};

/** select の値として使う文字列（SearchArea の <option value> と同じ規則） */
export const divisionOptionValue = (opt: Partial<OptionsSelectList> | null | undefined): string =>
  String(opt?.value ?? opt?.label ?? '');
